import { IProject, IUserObject } from 'types'

export const isFavourite = (projectId: string, favourites?: Partial<IProject>[]): boolean => {
  if (!favourites) return false
  return favourites.some((f) => f.publicId === projectId)
}

/**
 * Toggle a hub's starred state.
 *
 * Optimistic: the favourites update first so the star reacts immediately,
 * then the response replaces them, and a failure restores what was there before.
 * Only favourites are written: replaying the whole `user` read before the
 * request would undo anything else that changed while it was in flight.
 * Extracted from the home view so the sidebar can star a hub without a second
 * copy of the same logic drifting from it.
 */
export const toggleFavouriteHub = async (opts: {
  user: IUserObject
  projectId: string
  updateUser: (update: Partial<IUserObject>) => Promise<void> | void
  request: (args: { method: 'PUT'; endpoint: string }) => Promise<{ data: Partial<IProject>[] }>
  onError: (error: unknown) => void
}): Promise<void> => {
  const { user, projectId, updateUser, request, onError } = opts
  const alreadyFav = user.favourites.some((f: Partial<IProject>) => f.publicId === projectId)
  const nextFavs = alreadyFav
    ? user.favourites.filter((f: Partial<IProject>) => f.publicId !== projectId)
    : [...user.favourites, { publicId: projectId }]

  await updateUser({ favourites: nextFavs })

  try {
    const response = await request({ method: 'PUT', endpoint: `/user/hubs/${projectId}/favourite` })
    await updateUser({ favourites: response.data })
  } catch (error) {
    await updateUser({ favourites: user.favourites })
    onError(error)
  }
}
